/**
 * Route copy for `src/routes/aiTeam` (keys: `routes.aiTeam.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  aITeamPage: {
    creditsShort: '{{count}} cr',
    enabled: 'Enabled',
    enable: 'Enable',
    active: 'Active',
    enableName: 'Enable {{name}}',
    enableTheAgentToGiveIt: 'Enable the agent to give it a task',
    startATask: 'Start a task',
    aiAgent: 'AI agent',
    tooManyRequestsLimitTemporarilyReached: 'Too many requests — limit temporarily reached. Wait a moment, then try again.',
    couldNotLoadTheAiAgents: 'Could not load the AI agents. Try again later.',
    nameEnabled: '{{name}} enabled',
    nameDisabled: '{{name}} disabled',
    updatingTheAgentFailed: 'Updating the agent failed',
    aiTeam: 'AI team',
    yourVirtualAiAgentsEnableThem: 'Your virtual AI agents — enable them and give them tasks.',
    teamOrchestration: 'Team orchestration',
    aiActivity: 'AI activity',
    activeAgents: 'Active agents',
    costPerTask: 'Cost per task',
    runcostCr: '{{runCost}} cr',
    availableCredits: 'Available credits',
    unlimited: 'Unlimited',
    recentTasks: 'Recent tasks',
    enableAnAgentOrGiveIt: 'enable an agent or give it a task',
    loadingAiAgents: 'Loading AI agents…',
    noAiAgentAvailable: 'No AI agent available',
    personasWillBeAvailableOnceThe: 'Personas will be available once the AI registry is initialized.',
    viewAll: 'View all',
    noTaskStartedYetEnableAn: 'No task started yet. Enable an agent, then “Start a task”.',
  },
  auditTrailPage: {
    deliverableProduced: 'Deliverable produced',
    toolUsed: 'Tool used',
    failed: 'Failed',
    paused: 'Paused',
    resumed: 'Resumed',
    cancelled: 'Cancelled',
    runReplayed: 'Run replayed',
    taskRestarted: 'Task restarted',
    taskReplayed: 'Task replayed',
    escalated: 'Escalated',
    deliverableApprovedEvent: 'Deliverable approved',
    deliverableRejectedEvent: 'Deliverable rejected',
    linkVerifiedThePreviousHashMatches: 'Link verified — the previous hash matches.',
    breakThePreviousHashDoesNot: 'Break — the previous hash does not match.',
    outsideTheLoadedWindowNotVerifiable: 'Outside the loaded window — not verifiable here.',
    noChainedEventToVerifyYet: 'No chained event to verify yet.',
    chainVerified: 'Chain verified',
    chainBreakDetected: 'Chain break detected',
    checkedLinkS: '· {{checked}} link(s)',
    checked: 'checked',
    brokenBreakS: ' · {{broken}} break(s)',
    overTheLoadedWindow: 'over the loaded window',
    system: 'System',
    eventDetails: 'Event details',
    close: 'Close',
    sequence: 'Sequence',
    timestamp: 'Timestamp',
    agent: 'Agent',
    type: 'Type',
    summary: 'Summary',
    task: 'Task',
    run: 'Run',
    actor: 'Actor',
    aiAutomated: 'AI (automated)',
    hashChain: 'Hash chain',
    previousHashPrevHash: 'Previous hash (prev_hash)',
    genesis: '(genesis)',
    hashOfThisEntryEntryHash: 'Hash of this entry (entry_hash)',
    tooManyRequestsTryAgainIn: 'Too many requests — try again in a moment.',
    accessReservedForTheOrganizationS: 'Access reserved for the organization\'s owners and administrators.',
    couldNotLoadTheAuditLog: 'Could not load the audit log.',
    references: 'References',
    taskV: 'task {{v}}',
    runV: 'run {{v}}',
    chain: 'Chain',
    aiAuditLog: 'AI audit log',
    chronologicalTamperProofHistoryOfYour: 'Chronological, tamper-proof history of your AI team\'s activity.',
    loading: 'Loading…',
    restrictedAccess: 'Restricted access',
    theAuditLogIsReservedFor: 'The audit log is reserved for the organization\'s owners and administrators. Ask an administrator if you need access.',
    everyActionOfYourAiTeam: 'Every action of your AI team is recorded, then linked to the previous one by a hash chain — any later edit or deletion breaks the chain.',
    refresh: 'Refresh',
    theAuditLogIsNotAvailable: 'The audit log is not available on this environment yet.',
    filterByAgent: 'Filter by agent',
    allAgents: 'All agents',
    filterByEventType: 'Filter by event type',
    allTypes: 'All types',
    numberOfEventsLoaded: 'Number of events loaded',
    sizeOfTheLoadedWindowFor: 'Size of the loaded window for chain verification',
    nEvents: '{{n}} events',
    reverseTheChronologicalOrder: 'Reverse the chronological order',
    newestFirst: 'Newest first ↓',
    oldestFirst: 'Oldest first ↑',
    resetTheFilters: 'Reset the filters',
    event: 'event',
    shown: 'shown',
    ofLengthLoaded: 'of {{length}} loaded',
    loadingTheAuditLog: 'Loading the audit log…',
    noAuditEventYet: 'No audit event yet.',
    noEventMatchesTheseFilters: 'No event matches these filters.',
  },
  controlConsolePage: {
    queued: 'Queued',
    inProgress: 'In progress',
    awaitingApproval: 'Awaiting approval',
    paused: 'Paused',
    done: 'Done',
    failed: 'Failed',
    cancelled: 'Cancelled',
    scheduled: 'Scheduled',
    ready: 'Ready',
    blocked: 'Blocked',
    completed: 'Completed',
    cancelled2: 'Cancelled',
    paused2: 'paused',
    resumed2: 'resumed',
    interrupted2: 'interrupted',
    restartedNewClonedRun: 'restarted (new cloned run)',
    runVerb: 'Run {{verb}}.',
    replayingRunsIsNotAvailableOn: 'Replaying runs is not available on this environment yet.',
    actionNotPossible: 'Action not possible.',
    escalatedTaskSEscalated: '{{escalated}} task(s) escalated.',
    noOverdueTaskToEscalate: 'No overdue task to escalate.',
    couldNotEscalate: 'Could not escalate.',
    aiControl: 'AI control',
    aiTeamControlReportingConsole: 'AI team control & reporting console.',
    loading: 'Loading…',
    restrictedAccess: 'Restricted access',
    theAiControlConsoleIsReserved: 'The AI control console is reserved for the organization\'s owners and administrators. Ask an administrator if you need access.',
    steerLiveRunsTrackUsageAnd: 'Steer live runs, track usage and audit your AI team\'s activity.',
    dashboard: 'Dashboard',
    escalateEveryTaskPastItsDue: 'Escalate every task past its due date that is not yet escalated',
    escalating: 'Escalating…',
    escalateOverdueTasks: 'Escalate overdue tasks',
    refresh: 'Refresh',
    couldNotLoadTheReporting: 'Could not load the reporting.',
    loadingTheReporting: 'Loading the reporting…',
    runs: 'Runs',
    cumulativeCost: 'Cumulative cost',
    creditsCharged: 'Credits charged',
    activeAiStaff: 'Active AI staff',
    inputTokens: 'Input tokens',
    outputTokens: 'Output tokens',
    runsByStatus: 'Runs by status',
    noRuns: 'No runs.',
    tasksByStatus: 'Tasks by status',
    noTasks: 'No tasks.',
    liveRuns: 'Live runs',
    couldNotLoadTheRuns: 'Could not load the runs.',
    loadingRuns: 'Loading runs…',
    noRecentRun: 'No recent run.',
    run: 'Run',
    type: 'Type',
    status: 'Status',
    cost: 'Cost',
    created: 'Created',
    actions: 'Actions',
    pause: 'Pause',
    resume: 'Resume',
    interrupt: 'Interrupt',
    replay: 'Replay',
    auditLog: 'Audit log',
    filterByEventType: 'Filter by event type',
    allEvents: 'All events',
    couldNotLoadTheLog: 'Could not load the log.',
    loadingTheLog: 'Loading the log…',
    noEvents: 'No events',
    forThisFilter: ' for this filter',
  },
  dailyBatchSection: {
    dailyBatch: 'Daily batch',
    french: 'French',
  },
  deliverablesPage: {
    draft: 'Draft',
    delivered: 'Delivered',
    approved: 'Approved',
    dismissed: 'Dismissed',
    allStatuses: 'All statuses',
    untitledDeliverable: 'Untitled deliverable',
    preview: '👁 Preview',
    openTheObject: 'Open the object',
    objectUnavailable: 'Object unavailable',
    approve: 'Approve',
    reject: 'Reject',
    handled: 'Handled',
    aiAgent: 'AI agent',
    tooManyRequestsLimitTemporarilyReached: 'Too many requests — limit temporarily reached. Wait a moment, then try again.',
    couldNotLoadTheAiDeliverables: 'Could not load the AI deliverables. Try again later.',
    deliverableApproved: 'Deliverable approved',
    deliverableRejected: 'Deliverable rejected',
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    reviewingTheDeliverableFailedTryAgain: 'Reviewing the deliverable failed. Try again.',
    aiDeliverables: 'AI deliverables',
    everyDeliverableProducedByYourAgents: 'Every deliverable produced by your agents, to review and approve in one place.',
    filterByStatus: 'Filter by status',
    loadingDeliverables: 'Loading deliverables…',
    noDeliverables: 'No deliverables',
    noDeliverableMatchesThisStatusTry: 'No deliverable matches this status. Try another filter.',
    deliverablesProducedByYourAiAgents: 'Deliverables produced by your AI agents will appear here.',
    deliverable: 'deliverable',
    deliverable2: 'Deliverable',
    type: 'Type',
    status: 'Status',
    producer: 'Producer',
    date: 'Date',
    subject: 'Subject',
    review: 'Review',
    previewV: 'Preview — {{v}}',
    created: 'Created',
    targetService: 'Target service',
    targetObject: 'Target object',
    reviewedBy: 'Reviewed by',
    producedContent: 'Produced content',
    openTheObjectToInspectThe: 'Open the object to inspect the full content produced by the agent before approving or rejecting this deliverable.',
    reviewTheContentProducedAboveBefore: 'Review the content produced above before approving or rejecting this deliverable.',
  },
  launchTaskModal: {
    nameIsWorkingUntilTheGoal: '{{name}} is working until the goal is reached',
    nameStartedTheTask: '{{name}} started the task',
    thisAgentMustBeEnabledBefore: 'This agent must be enabled before starting a task.',
    startingTheTaskFailedTryAgain: 'Starting the task failed. Try again later.',
    startATaskName: 'Start a task — {{name}}',
    cancel: 'Cancel',
    launching: 'Launching…',
    startCostCr: 'Start ({{cost}} cr)',
    goalInstruction: 'Goal / instruction',
    describeWhatTheAgentShouldProduce: 'Describe what the agent should produce (a draft will be returned for you to approve). You can dictate by voice.',
    eGWrite3LinkedinPosts: 'E.g. Write 3 LinkedIn posts about our expertise',
    dictateTheGoal: 'Dictate the goal',
    examples: 'Examples',
    workUntilTheGoalIsReached: 'Work until the goal is reached',
    theAgentIteratesProduceSelfEvaluate: 'The agent iterates (produce → self-evaluate → improve) until the goal is judged reached. Each iteration uses credits.',
    maximumIterations: 'Maximum iterations',
    estimatedCost: 'Estimated cost',
    credits: 'credits',
    balance: ' · balance ∞',
    balanceVCr: ' · balance {{v}} cr',
    insufficientAiCreditsToStartThis: '⚡ Insufficient AI credits to start this task',
    topUpMyCredits: 'Top up my credits',
  },
  officePage: {
    capacityShort: 'cap. {{capacity}}',
    queued: 'Queued',
    inProgress: 'In progress',
    awaitingApproval: 'Awaiting approval',
    paused: 'Paused',
    done: 'Done',
    failed: 'Failed',
    cancelled: 'Cancelled',
    disabled: 'Disabled',
    load: 'Load',
    atCapacity: 'At capacity',
    enabled: 'Enabled',
    inactive: 'Inactive',
    load2: 'Load:',
    taskS: 'task(s)',
    runningTasksInProgress: '· {{running_tasks}} in progress',
    inProgress2: 'In progress:',
    chat: 'Chat',
    table: 'Table',
    activity: 'Activity',
    configure: 'Configure',
    couldNotLoadTheBoard: 'Could not load the board.',
    actionNotPossible: 'Action not possible.',
    restarted: 'restarted',
    replayed: 'replayed',
    taskAlreadyVerb: 'Task already {{verb}}.',
    taskVerb: 'Task {{verb}}.',
    couldNotRetry: 'Could not retry.',
    couldNotReplay: 'Could not replay.',
    boardName: 'Board — {{name}}',
    totalTaskS: '{{total}} task(s)',
    close: 'Close',
    loading: 'Loading…',
    noTasksYetStartATask: 'No tasks yet. Start a task from “AI team” or Orchestration.',
    hil: 'HIL',
    boardColBacklog: 'Backlog',
    boardColTodo: 'To do',
    boardColInProgress: 'In progress',
    boardColWaiting: 'Waiting',
    boardColDone: 'Done',
    boardColArchived: 'Closed',
    unassigned: 'Unassigned',
    cancel: 'Cancel',
    putTheTaskBackInThe: 'Put the task back in the run queue',
    retry: 'Retry',
    replayTheTaskAsANew: 'Replay the task as a new attempt',
    replay: 'Replay',
    role: 'Role',
    hierarchy: 'Hierarchy',
    capability: 'Capability',
    aiTeammateProfileUpdated: 'AI teammate profile updated.',
    couldNotSaveTheProfile: 'Could not save the profile.',
    configureName: 'Configure {{name}}',
    stepVLengthWizardSteps: 'Step {{v}}/{{length}} · {{WIZARD_STEPS}}',
    jobTitle: 'Job title',
    eGMarketingDirector: 'e.g. Marketing Director',
    department: 'Department',
    eGMarketing: 'e.g. Marketing',
    seniority: 'Seniority',
    reportsTo: 'Reports to',
    noneLeadership: '— None (leadership) —',
    setsTheReportingLineShownIn: 'Sets the reporting line shown in the org chart and the basis for upcoming delegation / escalation.',
    capacityConcurrentTasks: 'Capacity (concurrent tasks)',
    status: 'Status',
    mandateMission: 'Mandate (mission)',
    oneSentenceDescribingTheirMission: 'One sentence describing their mission.',
    previous: 'Previous',
    next: 'Next',
    saving: 'Saving…',
    save: 'Save',
    couldNotLoadTheActivity: 'Could not load the activity.',
    activityName: 'Activity — {{name}}',
    deliverables: 'Deliverables',
    openTheObject: 'Open the object',
    noDeliverableYet: 'No deliverable yet.',
    activityLog: 'Activity log',
    noActivityYet: 'No activity yet.',
    aiTeamTemporarilyUnavailableTryAgain: 'AI team temporarily unavailable. Try again in a moment.',
    couldNotLoadTheAiTeam: 'Could not load the AI team.',
    noDepartment: 'No department',
    aiOffice: 'AI office',
    yourVirtualStaffRolesOrgChart: 'Your virtual staff: roles, org chart, live workload.',
    loadingTheTeam: 'Loading the team…',
    aiTeammates: 'AI teammates',
    enabled2: 'Enabled',
    atWork: 'At work',
    departments: 'Departments',
    orgChart: 'Org chart',
    available: 'Available',
    liveTeamActivity: 'Live team activity',
    noRecentActivityToShow: 'No recent activity to show.',
    headcount: 'Headcount',
    allLength: 'All ({{length}})',
    teammate: 'teammate',
  },
  schedulesPage: {
    done: 'Done',
    succeeded: 'Succeeded',
    inProgress: 'In progress',
    failed: 'Failed',
    error: 'Error',
    skipped: 'Skipped',
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    theCadenceRruleIsInvalidCheck: 'The cadence (RRULE) is invalid. Check the rule and try again.',
    disabled: 'Disabled',
    disableTheSchedule: 'Disable the schedule',
    enableTheSchedule: 'Enable the schedule',
    active: 'Active',
    inactive: 'Inactive',
    nextRun: 'Next run',
    lastRun: 'Last run',
    lastStatus: 'Last status',
    deleting: 'Deleting…',
    delete: 'Delete',
    scheduleCreated: 'Schedule created',
    creatingTheScheduleFailedTryAgain: 'Creating the schedule failed. Try again.',
    previous: 'Previous',
    cancel: 'Cancel',
    continue: 'Continue',
    creating: 'Creating…',
    createTheSchedule: 'Create the schedule',
    newSchedule: 'New schedule',
    assignedStaff: 'Assigned staff',
    chooseAMemberOfTheVirtual: 'Choose a member of the virtual staff, or the whole team (several personas).',
    wholeTeamMultiPersona: 'Whole team (multi-persona)',
    recurringGoal: 'Recurring goal',
    describeTheMissionToRunAutomatically: 'Describe the mission to run automatically at every due date.',
    eGWriteAndPrepare3: 'E.g. Write and prepare 3 LinkedIn posts about our product news.',
    cadence: 'Cadence',
    preset: 'Preset',
    customRrule: 'Custom RRULE',
    recurrenceRuleIcalendarRrule: 'Recurrence rule (iCalendar RRULE)',
    eGFreqWeeklyBydayMo: 'E.g. FREQ=WEEKLY;BYDAY=MO,WE,FR — a single RRULE field, without a DTSTART prefix.',
    freqDailyInterval1: 'FREQ=DAILY;INTERVAL=1',
    cadencePreview: 'Cadence preview',
    theRuleMustContainAFrequency: 'The rule must contain a frequency (FREQ=…).',
    thisScheduleWillRunAutomaticallyAccordin: 'This schedule will run automatically according to the chosen cadence.',
    staff: 'Staff',
    wholeTeam: 'Whole team',
    objective: 'Objective',
    rrule: 'RRULE',
    couldNotLoadTheAiSchedules: 'Could not load the AI schedules. Try again later.',
    scheduleEnabled: 'Schedule enabled',
    scheduleDisabled: 'Schedule disabled',
    updatingTheScheduleFailed: 'Updating the schedule failed',
    deleteTheSchedule: 'Delete the schedule',
    deleteGoalItWillStopRunning: 'Delete “{{goal}}”? It will stop running automatically.',
    scheduleDeleted: 'Schedule deleted',
    deletingTheScheduleFailed: 'Deleting the schedule failed',
    aiSchedules: 'AI schedules',
    scheduleRecurringGoalsRunAutomaticallyBy: 'Schedule recurring goals run automatically by your AI staff.',
    loadingSchedules: 'Loading schedules…',
    noSchedulesYet: 'No schedules yet',
    clickNewScheduleToProgramA: 'Click “New schedule” to program a recurring goal.',
    schedulesCreatedByAnAdministratorWill: 'Schedules created by an administrator will appear here.',
    schedule: 'schedule',
    active2: 'active',
    ofLength: 'of {{length}}',
  },
  staffChatModal: {
    thisAgentIsNotEnabledEnable: 'This agent is not enabled — enable it in “AI team” before chatting with it.',
    agentNotFoundReloadThePage: 'Agent not found — reload the page and try again.',
    sendingTheMessageFailedTryAgain: 'Sending the message failed. Try again in a moment.',
    privateChat: 'Private chat',
    chatName: 'Chat — {{name}}',
    writeAMessageToName: 'Write a message to {{name}}…',
    yourMessage: 'Your message',
    sending: 'Sending…',
    send: 'Send',
    startTheConversationWithName: 'Start the conversation with {{name}}',
    askAQuestionOrGiveAn: 'Ask a question or give an instruction — the chat stays private.',
    nameIsTyping: '{{name}} is typing…',
  },
  taskActions: {
    taskDelegated: 'Task delegated.',
    couldNotDelegate: 'Could not delegate.',
    taskEscalated: 'Task escalated.',
    couldNotEscalate: 'Could not escalate.',
    delegate: 'Delegate',
    delegateTheTaskToAPersona: 'Delegate the task to a persona',
    persona: '— persona —',
    escalate: 'Escalate',
    escalateTheTaskToATeam: 'Escalate the task to a team member',
    escalate2: 'Escalate…',
    lead: '— lead —',
  },
  taskPoolBoard: {
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    thisTaskWasJustClaimedBy: 'This task was just claimed by someone else.',
    actionReservedForAdministratorsInsuffici: 'Action reserved for administrators (insufficient rights).',
    taskNotFoundReloadThePage: 'Task not found — reload the page and try again.',
    urgent: 'Urgent',
    high: 'High',
    normal: 'Normal',
    low: 'Low',
    taskCreated: 'Task created',
    creatingTheTaskFailedTryAgain: 'Creating the task failed. Try again.',
    createATask: 'Create a task',
    cancel: 'Cancel',
    creating: 'Creating…',
    create: 'Create',
    title: 'Title',
    eGFollowUpThisWeek: 'E.g. Follow up this week\'s cold leads',
    descriptionOptional: 'Description (optional)',
    priority: 'Priority',
    humanApproval: 'Human approval',
    requireApprovalBeforeRunning: 'Require approval before running',
    destination: 'Destination',
    poolUnassigned: 'Pool (unassigned)',
    anAgent: 'An agent',
    aTeam: 'A team',
    agent: 'Agent',
    noPersonaAvailable: 'No persona available',
    team: 'Team',
    willFanOutIntoOneTask: 'Will fan out into one task per seat on dispatch (nothing runs on assignment).',
    noTeamAvailable: 'No team available',
    memberCountSeatS: '({{member_count}} seat(s)',
    teamAssignedSeatCountTaskS: 'Team assigned — {{seat_count}} task(s) created',
    taskAssigned: 'Task assigned',
    assignmentFailedTryAgain: 'Assignment failed. Try again.',
    assignTitle: 'Assign — {{title}}',
    assigning: 'Assigning…',
    assign: 'Assign',
    approvalRequired: 'Approval required',
    claim: 'Claim',
    claimed: 'Claimed',
    sending: 'Sending…',
    dispatch: 'Dispatch',
    couldNotLoadTheTaskPool: 'Could not load the task pool. Try again later.',
    taskClaimed: 'Task claimed',
    claimingFailed: 'Claiming failed.',
    taskSentToTheCoordinator: 'Task sent to the coordinator',
    dispatchFailed: 'Dispatch failed.',
    taskPool: 'Task pool',
    unassignedTasksClaimableByAnAgent: 'Unassigned tasks, claimable by an agent, a team or yourself.',
    loadingTheTaskPool: 'Loading the task pool…',
    poolLength: 'Pool ({{length}})',
    emptyPool: 'Empty pool',
    noUnassignedTaskYet: 'No unassigned task yet.',
    assignedLength: 'Assigned ({{length}})',
    noAssignedTask: 'No assigned task',
    assignOrClaimATaskFrom: 'Assign or claim a task from the pool.',
    claimedByYou: 'Claimed by you',
    assignedOrClaimedLength: 'Assigned / claimed ({{length}})',
    cancelTaskAria: 'Cancel the task {{title}}',
    cancelTheTask: 'Cancel the task',
    cancelTaskConfirmMessage: 'Cancel “{{title}}”? It will leave the pool and will never be executed.',
    keep: 'Keep',
    taskCancelled: 'Task cancelled',
    cancellationFailed: 'Cancellation failed.',
    taskClaimedAwaitingDispatch: 'Task claimed — awaiting dispatch (nothing runs until « Dispatch »)',
    taskAssignedAwaitingDispatch: 'Task assigned — awaiting dispatch (nothing runs until « Dispatch »)',
    awaitingDispatchHint: 'Assigned, awaiting dispatch: nothing runs until you click « Dispatch ».',
    release: 'Release',
    releaseTaskAria: 'Release task {{title}} back to the pool',
    taskReleased: 'Task released — back in the pool',
    releaseFailed: 'Release failed.',
    deleteTask: 'Delete',
    deleteTaskAria: 'Delete task {{title}}',
    deleteTaskTitle: 'Delete the task',
    deleteTaskConfirmMessage: 'Permanently delete « {{title}} »? It never ran.',
    taskDeleted: 'Task deleted',
    deleteFailed: 'Deletion failed.',
    cancelledLength: 'Cancelled ({{length}})',
    cancelledHint: 'A cancelled task that never ran can be deleted; the history of a task that already ran is kept.',
    historyKept: 'History kept',
    dispatchConfirmTitle: 'Dispatch « {{title}} »',
    estimateLoading: 'Computing the estimate…',
    estimateFailed: 'Cost estimate unavailable.',
    estimatedCost: 'Estimated cost: {{credits}} credit(s)',
    estimateDetail: '{{count}} task(s) × {{per}} credit(s); maximum reserve {{max}} credit(s).',
    balanceAvailable: 'Available balance: {{available}} credit(s).',
    insufficientCredits: 'Not enough credits to run this task.',
    dispatchConfirmWarning: 'Execution starts immediately (real model call) and cannot be cancelled midway.',
    runNow: 'Start execution',
  },
  teamBuilderPage: {
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    actionReservedForAdministratorsInsuffici: 'Action reserved for administrators (insufficient rights).',
    notFoundReloadThePageAnd: 'Not found — reload the page and try again.',
    inheritedAutonomy: 'Inherited autonomy',
    addingTheRoleFailedTryAgain: 'Adding the role failed. Try again.',
    cancel: 'Cancel',
    adding: 'Adding…',
    add: 'Add',
    noPersonaAvailable: 'No persona available',
    enableAgentsInAiTeamBefore: 'Enable agents in “AI team” before composing a role.',
    rolePersona: 'Role (persona)',
    numberOfSeats: 'Number of seats',
    eG3ForAccountant3: 'E.g. 3 for “Accountant ×3”.',
    instanceLabelOptional: 'Instance label (optional)',
    a12SuffixWillBe: 'A #1, #2… suffix will be added automatically to each seat.',
    eGSeniorAccountant: 'E.g. “Senior accountant”.',
    eGSeniorAccountant2: 'E.g. Senior accountant',
    skillsOptional: 'Skills (optional)',
    overlaySpecificToThisTheseSeat: 'Overlay specific to this/these seat(s).',
    noSkillInTheRegistry: 'No skill in the registry.',
    toolsOptional: 'Tools (optional)',
    noToolInTheRegistry: 'No tool in the registry.',
    autonomyOptional: 'Autonomy (optional)',
    otherwiseInheritsThePersonaSSetting: 'Otherwise, inherits the persona\'s setting.',
    inheritedFromThePersona: 'Inherited from the persona',
    updatingTheSeatFailedTryAgain: 'Updating the seat failed. Try again.',
    editTheSeatV: 'Edit the seat — {{v}}',
    saving: 'Saving…',
    save: 'Save',
    instanceLabel: 'Instance label',
    skills: 'Skills',
    tools: 'Tools',
    autonomy: 'Autonomy',
    launchingTheTeamFailedTryAgain: 'Launching the team failed. Try again.',
    launchTheTeamName: 'Launch the team — {{name}}',
    launching: 'Launching…',
    launchTheMission: 'Launch the mission',
    theCoordinatorPlansOneTaskPer: 'The coordinator plans one task per seat ({{length}} in total), then runs them according to their respective autonomy.',
    missionGoal: 'Mission goal',
    eGPrepareTheMonthEnd: 'E.g. Prepare the month-end close and flag discrepancies.',
    newTeam: 'New team',
    continueChooseTheFirstRole: 'Continue — choose the first role',
    teamName: 'Team name',
    eGAccountingSquad: 'E.g. Accounting squad',
    teamLead: 'Team lead',
    seatV: 'Seat {{v}}',
    skill: 'skill',
    tool: 'tool',
    setAsLead: 'Set as lead',
    lead: 'Lead',
    edit: 'Edit',
    remove: 'Remove',
    couldNotLoadTheAiTeams: 'Could not load the AI teams. Try again later.',
    couldNotLoadTheTeamTry: 'Could not load the team. Try again.',
    teamNameCreated: 'Team “{{name}}” created',
    countSeatSAdded: '{{count}} seat(s) added',
    seatUpdated: 'Seat updated',
    vIsNowTheLead: '{{v}} is now the lead',
    promotionToLeadFailed: 'Promotion to lead failed.',
    removeThisSeat: 'Remove this seat',
    removeVFromTheTeam: 'Remove “{{v}}” from the team?',
    seatRemoved: 'Seat removed',
    removingTheSeatFailed: 'Removing the seat failed.',
    teamLaunchedTaskCountTaskS: 'Team launched — {{task_count}} task(s) scheduled.',
    teamLaunchedOnItsMission: 'Team launched on its mission.',
    aiTeamBuilder: 'AI team builder',
    teamBuilder: 'Team builder',
    composeYourTeamSRoleNumber: 'Compose your team\'s role × number of seats — each seat has its own label, skills/tools and autonomy.',
    teamTemplates: 'Team templates',
    taskPool: 'Task pool',
    teams: 'Teams',
    loading: 'Loading…',
    noTeam: 'No team',
    createYourFirstTeam: 'Create your first team.',
    noTeamYet: 'No team yet.',
    selectATeam: 'Select a team',
    chooseATeamOnTheLeft: 'Choose a team on the left, or create a new one.',
    loadingTheTeam: 'Loading the team…',
    addARole: 'Add a role',
    launchTheTeam: 'Launch the team',
    headcountLengthSeatS: 'Headcount — {{length}} seat(s)',
    noSeat: 'No seat',
    addARoleToGetStarted: 'Add a role to get started.',
    thisTeamIsEmpty: 'This team is empty.',
    newTeamSeedingteamnameFirstRole: 'New team “{{seedingTeamName}}” — first role',
    addARoleName: 'Add a role — {{name}}',
    thisSeat: 'This seat',
    thisSeat2: 'this seat',
  },
  teamHQPage: {
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    actionReservedForAdministratorsInsuffici: 'Action reserved for administrators (insufficient rights).',
    toggleAlwaysOn247Mode: 'Toggle always-on 24/7 mode',
    aiOffice: 'AI office',
    headcountOrgChartLiveWorkload: 'Headcount, org chart, live workload',
    aiSchedules: 'AI schedules',
    automaticRecurringGoals: 'Automatic recurring goals',
    controlConsole: 'Control console',
    superviseOngoingRuns: 'Supervise ongoing runs',
    orchestration: 'Orchestration',
    goalMultiPersonaPlanExecution: 'Goal → multi-persona plan → execution',
    couldNotLoadTheCommandCenter: 'Could not load the command center.',
    n247TeamEnabledContinuousCoordination: '24/7 team enabled — continuous coordination under way.',
    n247ModeDisabled: '24/7 mode disabled.',
    couldNotChangeThe247: 'Could not change the 24/7 mode.',
    enableTheWholeTeam: 'Enable the whole team',
    hireEnableAllTheAvailableAi: 'Hire (enable) all the available AI profiles at once?',
    enableAll: 'Enable all',
    activatedCountProfileSEnabledOut: '{{activated_count}} profile(s) enabled out of {{total}}.',
    couldNotEnableTheTeam: 'Could not enable the team.',
    teamMandateSaved: 'Team mandate saved.',
    couldNotSaveTheMandate: 'Could not save the mandate.',
    coordinatorSettingsSaved: 'Coordinator settings saved.',
    couldNotSaveTheSettings: 'Could not save the settings.',
    nameEnabled: '{{name}} enabled.',
    nameDisabled: '{{name}} disabled.',
    couldNotChangeNameSActivation: 'Could not change {{name}}\'s activation.',
    nameSAutonomyShortlabel: '{{name}}\'s autonomy: {{shortLabel}}.',
    couldNotChangeNameSAutonomy: 'Could not change {{name}}\'s autonomy.',
    applyToTheWholeTeam: 'Apply to the whole team',
    setEveryProfileSAutonomyTo: 'Set every profile\'s autonomy to “{{label}}”? CASL compliance still applies to every external send.',
    apply: 'Apply',
    autonomyShortlabelAppliedToLengthProfile: 'Autonomy “{{shortLabel}}” applied to {{length}} profile(s).',
    failedProfileSCouldNotBe: '{{failed}} profile(s) could not be updated.',
    couldNotApplyTheAutonomyGlobally: 'Could not apply the autonomy globally.',
    commandCenter247Team: 'Command center — 24/7 team',
    steerASelfCoordinatingVirtualTeam: 'Steer a self-coordinating virtual team continuously: enable, give mandates, set each profile\'s cadence and autonomy.',
    loadingTheCommandCenter: 'Loading the command center…',
    configurationUnavailable: 'Configuration unavailable',
    couldNotLoadTheTeamConfiguration: 'Could not load the team configuration. Try again later.',
    n247ModeAlwaysOn: '24/7 mode — Always on',
    active: 'Active',
    paused: 'Paused',
    theTeamWorksAndCoordinatesContinuously: 'The team works and coordinates continuously, without intervention.',
    enableSoTheTeamCoordinatesAnd: 'Enable so the team coordinates and runs its missions continuously.',
    profilesInTheTeam: 'Profiles in the team',
    enabledProfiles: 'Enabled profiles',
    cycleCadence: 'Cycle cadence',
    creditCapDay: 'Credit cap / day',
    hireTheWholeTeam: 'Hire the whole team',
    enablesEveryAvailableAiProfileIn: 'Enables every available AI profile in one click so they start working.',
    enabling: 'Enabling…',
    teamMandate: 'Team mandate',
    thePermanentGoalTheCoordinatorWorks: 'The permanent goal the coordinator works toward at every cycle.',
    permanentMandate: 'Permanent mandate',
    describeTheTeamSCoreMission: 'Describe the team\'s core mission (e.g. grow awareness and generate qualified leads).',
    eGGrowOurLinkedinPresence: 'E.g. Grow our LinkedIn presence and keep feeding the pipeline.',
    saving: 'Saving…',
    saveTheMandate: 'Save the mandate',
    coordinatorSettings: 'Coordinator settings',
    setThe247TeamS: 'Set the 24/7 team\'s work pace and budget.',
    cycleFrequencyMinutes: 'Cycle frequency (minutes)',
    intervalBetweenTwoCoordinationCyclesMin: 'Interval between two coordination cycles (min. 1).',
    missionsPerCycle: 'Missions per cycle',
    maximumNumberOfMissionsGeneratedPer: 'Maximum number of missions generated per cycle (min. 1).',
    limitOfCreditsUsedPerDay: 'Limit of credits used per day. Empty = unlimited.',
    unlimited: 'Unlimited',
    theFrequencyAndTheNumberOf: 'The frequency and the number of missions must be integers ≥ 1; the cap, a number ≥ 0 (or empty).',
    saveTheSettings: 'Save the settings',
    autonomyPerProfile: 'Autonomy — per profile',
    chooseForEachProfileHowFar: 'Choose for each profile how far it acts on its own. Every option is available; CASL compliance still applies to every external send.',
    l0StopEverythingSwitchForThis: 'L0 = “stop everything” switch for this profile; nothing runs on its own.',
    l4AutoPublishesCaslComplianceStill: 'L4 = auto-publishes; CASL compliance still applies.',
    l5NoApprovalExternalSendsIncluded: 'L5 = no approval, external sends included; CASL compliance still applies.',
    applyADefaultLevelToThe: 'Apply a default level to the whole team',
    setsTheSameLevelForEvery: 'Sets the same level for every profile; you can then fine-tune profile by profile.',
    applying: 'Applying…',
    noProfileInTheTeam: 'No profile in the team',
    clickEnableTheWholeTeamTo: 'Click “Enable the whole team” to hire your AI profiles.',
    profilesEnabledByAnAdministratorWill: 'Profiles enabled by an administrator will appear here.',
    disableName: 'Disable {{name}}',
    enableName: 'Enable {{name}}',
    inactive: 'Inactive',
    nameSAutonomyLevel: '{{name}}\'s autonomy level',
    coordinatorStateLive: 'Coordinator state (live)',
    continuousRefresh: 'Continuous refresh',
    refresh: 'Refresh',
    missionsCreatedLastCycle: 'Missions created (last cycle)',
    duplicatesSkipped: 'Duplicates skipped',
    creditsLastCycle: 'Credits (last cycle)',
    runsInProgress: 'Runs in progress',
    lastCycle: 'Last cycle',
    nextCycle: 'Next cycle',
    mode: 'Mode',
    n247Active: '24/7 active',
    coordinatorStateUnavailableRightNow: 'Coordinator state unavailable right now.',
    approvalQueue: 'Approval queue',
    deliverablesProducedByTheTeamAwaiting: 'Deliverables produced by the team awaiting your review.',
    openTheQueue: 'Open the queue',
    quickAccess: 'Quick access',
    hireEnableAllWithDefaultAutonomy: 'Hire (enable) all the available AI profiles at once? Their autonomy will be set to the default levels: L4/L3 (act then notify, without prior approval) for internal and content profiles, L2 (approval before any outbound action) for prospecting.',
    enable247ModeQuestion: 'Enable 24/7 mode?',
    enable247ModeMessage: 'The AI team will work and coordinate continuously, without intervention: it will launch real tasks, consume credits and may act according to each profile’s autonomy level.',
    enable247: 'Enable 24/7',
    disable247ModeQuestion: 'Disable 24/7 mode?',
    disable247ModeMessage: 'The team will stop coordinating continuously. Tasks already in progress will finish; no new mission will be launched automatically.',
    disable247: 'Disable 24/7',
  },
  teamsPage: {
    archived: 'Archived',
    member: 'member',
    automaticallyAssembledTeam: 'Automatically assembled team',
    noMembers: 'No members',
    lead: 'Lead:',
    launchTheTeam: 'Launch the team',
    archiving: 'Archiving…',
    archive: 'Archive',
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    ownerOrAdministratorRoleRequiredTo: 'Owner or administrator role required to launch a team.',
    thisTeamCannotBeFoundReload: 'This team cannot be found. Reload the page and try again.',
    aGoalIsRequiredToLaunch: 'A goal is required to launch the team.',
    theAiCircuitBreakerIsOpen: 'The AI circuit breaker is open for this account — launches are suspended. Try again later.',
    planningTheTeamFailedTryAgain: 'Planning the team failed. Try again in a moment.',
    launchingTheTeamFailedTryAgain: 'Launching the team failed. Try again later.',
    launchTheTeamName: 'Launch the team — {{name}}',
    cancel: 'Cancel',
    launching: 'Launching…',
    launchTheMission: 'Launch the mission',
    describeTheMissionTheCoordinatorPlans: 'Describe the mission: the coordinator plans a multi-persona plan across the {{member_count}} member(s)',
    ofTheTeamThenRunsIt: 'of the team, then runs it. You will follow the progress live.',
    missionGoal: 'Mission goal',
    whatTheTeamMustAccomplishBe: 'What the team must accomplish — be precise about the expected deliverable.',
    eGPrepareAndScheduleA: 'E.g. Prepare and schedule a week of LinkedIn content about our new offer.',
    contextOptional: 'Context (optional)',
    audienceConstraintsReferencesTonePassedT: 'Audience, constraints, references, tone… — passed to the planner.',
    eGB2bSmbTargetIn: 'E.g. B2B SMB target in Quebec, professional yet warm tone, avoid jargon.',
    automatic: 'Automatic',
    describeAGoalTheCoordinatorAssembles: 'Describe a goal — the coordinator assembles the team for you.',
    manualSelection: 'Manual selection',
    chooseThePersonasThatMakeUp: 'Choose the personas that make up the team yourself.',
    sized: 'Sized',
    giveASizeAndAFocus: 'Give a size and a focus — the team is calibrated.',
    teamLeadOptional: 'Team lead (optional)',
    noLeadDesignated: 'No lead designated',
    teamNameCreated: 'Team “{{name}}” created',
    ownerOrAdministratorRoleRequiredTo2: 'Owner or administrator role required to create a team.',
    creatingTheTeamFailedTryAgain: 'Creating the team failed. Try again.',
    previous: 'Previous',
    continue: 'Continue',
    creating: 'Creating…',
    createTheTeam: 'Create the team',
    createATeam: 'Create a team',
    howWouldYouLikeToBuild: 'How would you like to build the team?',
    teamGoal: 'Team goal',
    describeTheMissionTheCoordinatorWill: 'Describe the mission — the coordinator will choose the right personas.',
    eGLaunchAB2bAcquisition: 'E.g. Launch a B2B acquisition campaign on LinkedIn and by email.',
    selectTheMembers: 'Select the members',
    lengthSelected: '{{length}} selected',
    noPersonaAvailable: 'No persona available',
    enableAgentsInAiTeamBefore: 'Enable agents in “AI team” before composing a team.',
    teamSize: 'Team size',
    numberOfPersonasToAssemble: 'Number of personas to assemble.',
    orientation: 'Orientation',
    theProfileOrSpecialtyWantedFor: 'The profile or specialty wanted for this team.',
    eGFocusedOnContentAnd: 'E.g. Focused on content and community management, warm tone.',
    teamName: 'Team name',
    eGGrowthSquad: 'E.g. Growth squad',
    mode: 'Mode',
    members: 'Members',
    size: 'Size',
    objective: 'Objective',
    tooManyRequestsLimitTemporarilyReached: 'Too many requests — limit temporarily reached. Wait a moment, then try again.',
    couldNotLoadTheAiTeams: 'Could not load the AI teams. Try again later.',
    archiveTheTeam: 'Archive the team',
    archiveNameItWillNoLonger: 'Archive “{{name}}”? It will no longer appear in the active selection.',
    teamNameArchived: 'Team “{{name}}” archived',
    ownerOrAdministratorRoleRequiredTo3: 'Owner or administrator role required to archive a team.',
    archivingTheTeamFailed: 'Archiving the team failed',
    teamLaunchedTaskCountTaskS: 'Team launched — {{task_count}} task(s) scheduled across {{member_count}} member(s).',
    teamLaunchedOnItsMission: 'Team launched on its mission.',
    aiTeams: 'AI teams',
    groupYourVirtualAgentsIntoTeams: 'Group your virtual agents into teams dedicated to a goal.',
    loadingTeams: 'Loading teams…',
    noTeamYet: 'No team yet',
    clickCreateATeamToCompose: 'Click “Create a team” to compose your first AI squad.',
    teamsCreatedByAnAdministratorWill: 'Teams created by an administrator will appear here.',
    team: 'team',
    active: 'active',
  },
  templateGallery: {
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    actionReservedForAdministratorsInsuffici: 'Action reserved for administrators (insufficient rights).',
    templateNotFoundReloadThePage: 'Template not found — reload the page and try again.',
    role: 'Role',
    savingTheTemplateFailedTryAgain: 'Saving the template failed. Try again.',
    editName: 'Edit — {{name}}',
    newTeamTemplate: 'New team template',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    createTemplate: 'Create template',
    templateName: 'Template name',
    eGStandardAccountingSquad: 'E.g. Standard accounting squad',
    scope: 'Scope',
    aPlatformTeamCanBeInstantiated: 'A platform team can be instantiated by every tenant.',
    platformTemplateAllTenants: 'Platform template (all tenants)',
    descriptionOptional: 'Description (optional)',
    whatIsThisTemplateFor: 'What is this template for?',
    rolesLength: 'Roles ({{length}})',
    addARole: '+ Add a role',
    noPersonaAvailable: 'No persona available',
    enableAgentsInAiTeamBefore: 'Enable agents in “AI team” before composing a template.',
    noRole: 'No role',
    addAtLeastOneRole: 'Add at least one role.',
    remove: 'Remove',
    instanceLabelOptional: 'Instance label (optional)',
    autonomyInheritedFromThePersona: 'Autonomy inherited from the persona',
    instantiatingTheTemplateFailedTryAgain: 'Instantiating the template failed. Try again.',
    instantiateName: 'Instantiate — {{name}}',
    creating: 'Creating…',
    createTheTeam: 'Create the team',
    createsARealTeamWith: 'Creates a real team with',
    seatS: 'seat(s)',
    basedOnThisTemplate: 'based on this template.',
    newTeamName: 'New team name',
    goalOptional: 'Goal (optional)',
    platform: 'Platform',
    firm: 'Firm',
    inactive: 'Inactive',
    instantiate: 'Instantiate',
    edit: 'Edit',
    delete: 'Delete',
    couldNotLoadTheTemplatesTry: 'Could not load the templates. Try again later.',
    templateNameUpdated: 'Template “{{name}}” updated',
    templateNameCreated: 'Template “{{name}}” created',
    deleteTheTemplate: 'Delete the template',
    disableNameItWillNoLonger: 'Disable “{{name}}”? It will no longer appear in the gallery.',
    templateNameDeleted: 'Template “{{name}}” deleted',
    deletingTheTemplateFailed: 'Deleting the template failed.',
    teamCreatedFromTheTemplate: 'Team created from the template',
    teamTemplates: 'Team templates',
    instantiateAPreconfiguredTeamInOne: 'Instantiate a preconfigured team in one click, or compose a reusable template.',
    teamBuilder: 'Team builder',
    newTemplate: 'New template',
    loadingTemplates: 'Loading templates…',
    noTemplateYet: 'No template yet',
    clickNewTemplateToComposeYour: 'Click “New template” to compose your first reusable template.',
    templatesCreatedByAnAdministratorWill: 'Templates created by an administrator will appear here.',
    platformTemplates: 'Platform templates',
    myFirmSTemplates: 'My firm\'s templates',
    aiTeamsNotIncludedInYourPlan: 'AI teams are not included in your plan.',
    instantiateLocked: '🔒 Instantiate',
    reservedForOwnersAndAdmins: 'Reserved for owners and administrators.',
    instantiationReservedForOwnersAdmins: 'Instantiation reserved for owners / administrators',
    aiTeamsTemplateInstantiation: 'AI teams (template instantiation)',
  },
};

export default en;
